import type {
  HeatmapCell,
  HeatmapData,
  HeatmapTier,
  PersonDayLoad,
  PlannedWeekData,
  StaffMember,
  Weekday
} from '../types/weekOverview';
import { HEATMAP_THRESHOLDS, WEEKDAYS_ORDER } from '../constants/weekOverviewConstants';

// Classifies a skill/person day utilization into a heatmap tier. Mirrors the v4
// original: green only below 60%, red at 95%+ (see HEATMAP_THRESHOLDS).
export function getHeatmapTier(utilizationPercent: number, isClosed: boolean): HeatmapTier {
  if (isClosed) return 'closed';
  if (utilizationPercent >= HEATMAP_THRESHOLDS.full) return 'critical';
  if (utilizationPercent >= HEATMAP_THRESHOLDS.tight) return 'full';
  if (utilizationPercent >= HEATMAP_THRESHOLDS.healthy) return 'tight';
  return 'healthy';
}

// A cell nobody is available for: the person does not work that day (or the
// whole skill's staff is off), so there are no available hours to book against.
// Its 0% is an artefact, not spare capacity, so it renders grey like a closed
// day and stays out of the week total.
export function isCellUnavailable(cell: HeatmapCell): boolean {
  return !cell.isClosed && cell.availableHours === 0;
}

// Grey, non-activatable cells: the location is closed, or nobody works.
export function isCellInactive(cell: HeatmapCell): boolean {
  return cell.isClosed || isCellUnavailable(cell);
}

function emptyCell(skillId: string, staffId: string | null, day: Weekday, isClosed: boolean): HeatmapCell {
  return {
    skillId,
    staffId,
    day,
    bookedHours: 0,
    availableHours: 0,
    utilizationPercent: 0,
    staffScheduled: 0,
    staffOnLeave: 0,
    isClosed
  };
}

// Builds the heatmap-shaped view from the live week data. The heatmap aggregates
// each skill's qualified people per day (utilization = their booked ÷ available
// hours) and keeps the per-person breakdown for the expandable sub-rows. Days
// the location is closed (closedDays) and days without person-day loads render
// as closed ("—") cells.
export function buildHeatmapData(weekData: PlannedWeekData): HeatmapData {
  const closedDaySet = new Set<Weekday>(weekData.closedDays);

  // Days with at least one person-day load. An open day without any (no staff
  // in the snapshot) is closed too, on top of closedDays.
  const daysWithData = new Set<Weekday>(weekData.personDayLoads.map((load) => load.day));
  const isDayClosed = (day: Weekday): boolean => closedDaySet.has(day) || !daysWithData.has(day);

  // Group each person's day loads for quick lookup.
  const loadsByStaffDay = new Map<string, PersonDayLoad>();
  for (const load of weekData.personDayLoads) {
    loadsByStaffDay.set(`${load.staffId}|${load.day}`, load);
  }

  // Staff grouped by each skill they can perform.
  const staffBySkillId: Record<string, StaffMember[]> = {};
  for (const skill of weekData.skills) staffBySkillId[skill.id] = [];
  for (const staff of weekData.staff) {
    for (const skillId of staff.skillIds) {
      if (staffBySkillId[skillId]) staffBySkillId[skillId].push(staff);
    }
  }

  const heatmapCells: HeatmapCell[] = [];
  const staffHeatmapCells: HeatmapCell[] = [];

  for (const skill of weekData.skills) {
    const skillStaff = staffBySkillId[skill.id] ?? [];

    for (const day of WEEKDAYS_ORDER) {
      const closed = isDayClosed(day);

      // Per-person sub-row cells (duplicated under each skill the person has, so
      // an expanded skill shows that skill's people — matches v4's staffHeatmapCells).
      let booked = 0;
      let available = 0;
      let scheduled = 0;
      let onLeave = 0;

      for (const staff of skillStaff) {
        const load = loadsByStaffDay.get(`${staff.id}|${day}`);
        if (closed || !load) {
          staffHeatmapCells.push(emptyCell(skill.id, staff.id, day, closed));
          continue;
        }
        staffHeatmapCells.push({
          skillId: skill.id,
          staffId: staff.id,
          day,
          bookedHours: Number(load.bookedHours.toFixed(1)),
          availableHours: Number(load.availableHours.toFixed(1)),
          utilizationPercent: load.utilizationPercent,
          staffScheduled: load.isAbsent ? 0 : 1,
          staffOnLeave: load.isAbsent ? 1 : 0,
          isClosed: false
        });
        if (load.isAbsent) {
          onLeave += 1;
        } else {
          scheduled += 1;
          booked += load.bookedHours;
          available += load.availableHours;
        }
      }

      if (closed) {
        heatmapCells.push(emptyCell(skill.id, null, day, true));
        continue;
      }

      const utilizationPercent = available > 0 ? Math.round((booked / available) * 100) : 0;
      heatmapCells.push({
        skillId: skill.id,
        staffId: null,
        day,
        bookedHours: Number(booked.toFixed(1)),
        availableHours: Number(available.toFixed(1)),
        utilizationPercent,
        staffScheduled: scheduled,
        staffOnLeave: onLeave,
        isClosed: false
      });
    }
  }

  return {
    skills: weekData.skills,
    staffBySkillId,
    heatmapCells,
    staffHeatmapCells
  };
}
